import { useState, useEffect, useCallback, useContext } from 'react'
import useLastEventType from '../../../FSM/useLastEventType'

import CONFIG from '../../../config'
import { Events } from '../../../FSM/shared/constants'
import { QUOTE_ID, SELECTED_QUOTE_MOTOR_REGISTRATION_NUMBER } from '../../../constants'
import { FlowServiceType } from '../../../types/interface/fsm.interface'
import { normalizeMotorRegistrationNumber } from '@dg-util'
import BrandConfig from 'BrandConfig'
import useReactQueryAuth from '../../../hooks/shared/useReactQueryAuth'
import { AuthRegularV2InputType } from '../../../types/ApiV2'
import apiV2 from '../../../service/apiV2.service'
import { Context } from '@dg-shared/ContextProvider'
import { UserAuthInput } from '../../../types'
import messagesQueue from '../../../service/messagesQueue'
import { ProductName } from '../../../types/interface/quote.interface'

interface HookAuth {
  loading: boolean
  errorType: string
  errorVipps: boolean
  vippsLink: string
  authHandler(data: UserAuthInput): void
  isReCaptchaReady: boolean
  isReCaptchaUnavailable: boolean
}

// How long to wait for the async-loaded reCAPTCHA script before giving up. Without a bound the
// page sits on a spinner forever when the script never arrives (DFE-4106).
const RECAPTCHA_POLL_INTERVAL_MS = 100
const RECAPTCHA_READY_TIMEOUT_MS = 10000
const RECAPTCHA_MAX_POLL_ATTEMPTS = RECAPTCHA_READY_TIMEOUT_MS / RECAPTCHA_POLL_INTERVAL_MS
// `execute` does a network round-trip to Google, so it needs its own bound — otherwise a blocked
// or hanging request leaves the user on a spinner *after* filling the form.
const RECAPTCHA_EXECUTE_TIMEOUT_MS = 10000

// reCAPTCHA v3 tokens expire after 120s and are single-use, so one must be minted per submit
// rather than once on mount. See DFE-4106.
const executeReCaptcha = (): Promise<string> => {
  const mintToken = new Promise<string>((resolve, reject) => {
    if (!window.grecaptcha?.ready) {
      reject(new Error('reCaptcha is not available'))

      return
    }

    window.grecaptcha.ready(async () => {
      try {
        resolve(
          (await window.grecaptcha.execute(CONFIG.RECAPTCHA_SITE_KEY, {
            action: 'submit',
          })) as string
        )
      } catch (error) {
        reject(error)
      }
    }, undefined)
  })

  const timeout = new Promise<string>((_, reject) =>
    setTimeout(() => reject(new Error('reCaptcha timed out')), RECAPTCHA_EXECUTE_TIMEOUT_MS)
  )

  return Promise.race([mintToken, timeout])
}

const useLogic = (service: FlowServiceType): HookAuth => {
  const [loading, setLoading] = useState<boolean>(true)
  const [errorVipps, setErrorVipps] = useState<boolean>(false)
  const [vippsLink, setVippsLink] = useState<string>('')
  const stateEventType = useLastEventType(service)
  const [errorType, setErrorType] = useState<string>(null)
  const [isReCaptchaReady, setIsReCaptchaReady] = useState<boolean>(false)
  const [isReCaptchaUnavailable, setIsReCaptchaUnavailable] = useState<boolean>(false)

  // Check current FSM state event type for errorType
  useEffect(() => {
    // Check error events in messages queue
    const messageQueueAuthError = messagesQueue.getMessage(Events.AUTH_ERROR)
    const messageQueueSessionError = messagesQueue.getMessage(Events.SESSION_EXPIRED)

    if (messageQueueAuthError || messageQueueSessionError) {
      // Update eventType for correct error handling
      setErrorType(messageQueueAuthError?.id || messageQueueSessionError?.id)
    }

    // Check error events in stateEventType
    if (
      [Events.WRONG_PRODUCT_ID, Events.AUTH_ERROR, Events.SESSION_EXPIRED].includes(
        stateEventType as Events
      )
    ) {
      setErrorType(stateEventType)
    }

    // Cleanup on navigation
    if ([Events.NEXT, Events.BACK].includes(stateEventType as Events)) {
      setErrorType(null)
    }
  }, [stateEventType])

  const { VIPPS_AUTH } = BrandConfig
  const { authRegular, isAuthRegularLoading } = useReactQueryAuth()
  const { setContext } = useContext(Context)

  useEffect(() => {
    setLoading(isAuthRegularLoading)
  }, [isAuthRegularLoading])

  // Wait for the async-loaded reCAPTCHA script to become available. Only gates rendering — the
  // token itself is minted per submit in authHandler.
  useEffect(() => {
    if (window.grecaptcha?.ready) {
      setIsReCaptchaReady(true)

      return
    }

    let attempts = 0
    const intervalId = setInterval(() => {
      attempts += 1

      if (window.grecaptcha?.ready) {
        clearInterval(intervalId)
        setIsReCaptchaReady(true)
      } else if (attempts >= RECAPTCHA_MAX_POLL_ATTEMPTS) {
        // Give up rather than spin forever, and surface it so the page renders an error.
        clearInterval(intervalId)
        setIsReCaptchaUnavailable(true)
        setErrorType(Events.AUTH_ERROR)
      }
    }, RECAPTCHA_POLL_INTERVAL_MS)

    return () => {
      clearInterval(intervalId)
    }
  }, [])

  const authHandler = useCallback(
    async (userInput: AuthRegularV2InputType) => {
      setLoading(true)

      let reCaptchaToken: string

      try {
        // Fresh token per attempt, so a retry after a failure is not a replay of a dead one.
        reCaptchaToken = await executeReCaptcha()
      } catch (error) {
        console.error('ReCaptcha execution error', error)
        setErrorType(Events.AUTH_ERROR)
        setLoading(false)

        return
      }

      await authRegular({ ...userInput, reCaptchaToken })

      if (userInput.regNum) {
        setContext({
          [SELECTED_QUOTE_MOTOR_REGISTRATION_NUMBER]: normalizeMotorRegistrationNumber(
            userInput.regNum
          ),
        })
      }

      setLoading(false)
      service.send({ type: Events.NEXT })
    },
    [authRegular, service, setContext]
  )

  const getVipps = async () => {
    try {
      const { vLink, errVipps } = await apiV2.getVippsLink()
      const error = !!errVipps

      return error ? setErrorVipps(error) : setVippsLink(vLink)
    } catch {
      // Fire-and-forget in the effect below: an uncaught rejection here (e.g. network failure)
      // would otherwise surface as an unhandled promise rejection instead of the error UI.
      setErrorVipps(true)
    }
  }

  useEffect(() => {
    if (VIPPS_AUTH) {
      getVipps()
    }
  }, [VIPPS_AUTH])

  // Reset previously selected values
  useEffect(() => {
    if (ProductName.TRAVEL) {
      const resetedValues = {
        [SELECTED_QUOTE_MOTOR_REGISTRATION_NUMBER]: '',
        [QUOTE_ID]: '',
      }
      setContext(resetedValues)
    }
  }, [setContext])

  return {
    errorType,
    loading,
    errorVipps,
    vippsLink,
    authHandler,
    isReCaptchaReady,
    isReCaptchaUnavailable,
  }
}

export default useLogic
